/**
 * Credits realtime — a thin WebSocket subscriber that turns the backend's
 * server-pushed `credits:*` frames into the existing in-app window-event bus.
 *
 * The ai-orchestrator emits `credits:balance` / `credits:low_balance` /
 * `credits:topped_up` on every settle/debit/topup (see credits/service.py
 * `_emit_credit_event`) over the `/ws/chat` WebSocket. This module connects
 * once (shell-wide, from {@link CreditGuardHost}), filters frames to the
 * caller's own tenant, and re-emits them as:
 *   - {@link AI_CREDITS_UPDATED_EVENT}  → badge + host re-fetch the wallet
 *   - {@link AI_CREDITS_TOPPED_UP_EVENT} → host clears the out-of-credits dialog
 *     and the paused-runs panel refreshes/auto-clears
 *
 * It REPLACES the 60s balance poll as the primary transport (the poll stays only
 * as a slow backstop when the socket is not connected). Fail-safe: any error in
 * connecting/parsing degrades silently to the poll — a browser without a working
 * socket keeps working exactly as before.
 */
import { getConfig } from '@/config';
import { getAccessToken } from '@/auth/oidc';

import { requestCreditsRefresh } from './useCreditsStore';

/** Fired when the backend confirms a top-up (auto-clears refill UI + resumes). */
export const AI_CREDITS_TOPPED_UP_EVENT = 'psn:ai-credits-topped-up';
/** Fired on a pushed low-balance frame (proactive nudge without a poll). */
export const AI_CREDITS_LOW_EVENT = 'psn:ai-credits-low';

let socket: WebSocket | null = null;
let connected = false;
let stopped = false;
let backoff = 1000;
let reconnectTimer: number | null = null;

export function isCreditsRealtimeConnected(): boolean {
  return connected;
}

/** Best-effort tenant claim from the JWT (frames are cross-tenant broadcasts). */
function tenantFromToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? ''));
    return (
      payload.tenant_id ??
      payload.tenantId ??
      payload.tid ??
      payload.org_id ??
      null
    );
  } catch {
    return null;
  }
}

function dispatch(name: string, detail?: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    /* ignore */
  }
}

function handleFrame(raw: string, myTenant: string | null): void {
  let msg: { type?: string; data?: Record<string, unknown> };
  try {
    msg = JSON.parse(raw);
  } catch {
    return;
  }
  const type = msg?.type;
  if (typeof type !== 'string' || !type.startsWith('credits:')) return;
  const data = (msg.data ?? {}) as Record<string, unknown>;
  // broadcast_to_all is cross-tenant; only react to our own tenant's frames.
  const frameTenant = (data.tenant_id as string) ?? null;
  if (myTenant && frameTenant && frameTenant !== myTenant) return;

  if (type === 'credits:topped_up') {
    dispatch(AI_CREDITS_TOPPED_UP_EVENT, data);
    requestCreditsRefresh();
  } else if (type === 'credits:low_balance') {
    dispatch(AI_CREDITS_LOW_EVENT, data);
    requestCreditsRefresh();
  } else if (type === 'credits:balance') {
    requestCreditsRefresh();
  }
}

async function open(): Promise<void> {
  if (stopped || socket) return;
  let token: string | null = null;
  try {
    token = await getAccessToken();
  } catch {
    token = null;
  }
  if (!token) {
    scheduleReconnect();
    return;
  }
  const myTenant = tenantFromToken(token);
  let base = '';
  try {
    base = getConfig().wsUrl;
  } catch {
    return; // config not loaded — nothing to do
  }
  if (!base) return;
  const url = `${base.replace(/\/$/, '')}/ws/chat?token=${encodeURIComponent(token)}`;
  try {
    const ws = new WebSocket(url);
    socket = ws;
    ws.onopen = () => {
      connected = true;
      backoff = 1000;
    };
    ws.onmessage = (ev) => handleFrame(String(ev.data), myTenant);
    ws.onclose = () => {
      connected = false;
      socket = null;
      scheduleReconnect();
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
  } catch {
    socket = null;
    scheduleReconnect();
  }
}

function scheduleReconnect(): void {
  if (stopped || reconnectTimer != null) return;
  const delay = Math.min(backoff, 30_000);
  backoff = Math.min(backoff * 2, 30_000);
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    void open();
  }, delay);
}

/** Connect the credits realtime socket (idempotent). Returns a disconnect fn. */
export function connectCreditsRealtime(): () => void {
  stopped = false;
  void open();
  return () => {
    stopped = true;
    connected = false;
    if (reconnectTimer != null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (socket) {
      try {
        socket.close();
      } catch {
        /* ignore */
      }
      socket = null;
    }
  };
}
